import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import type { AgentWorkflow, WorkflowStep } from './types';

interface WorkflowModalProps {
  workflow: AgentWorkflow | null;
  onClose: () => void;
  onSaved: () => void;
}

const DEFAULT_STEPS: WorkflowStep[] = [
  { tool: 'engine_get_session_context', purpose: 'Bootstrap session memory' },
];

function stepsToJson(steps: WorkflowStep[]): string {
  return JSON.stringify(steps, null, 2);
}

function parseSteps(raw: string): WorkflowStep[] {
  const parsed = JSON.parse(raw) as unknown;
  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new Error('steps must be a non-empty JSON array');
  }
  return parsed as WorkflowStep[];
}

const WorkflowModal: React.FC<WorkflowModalProps> = ({ workflow, onClose, onSaved }) => {
  const isEdit = Boolean(workflow?.id);
  const editingActive = workflow?.status === 'active';
  const [slug, setSlug] = useState(workflow?.slug ?? '');
  const [title, setTitle] = useState(workflow?.title ?? '');
  const [triggers, setTriggers] = useState((workflow?.triggers ?? []).join(', '));
  const [stepsJson, setStepsJson] = useState(stepsToJson(workflow?.steps ?? DEFAULT_STEPS));
  const [successShape, setSuccessShape] = useState(workflow?.success_shape ?? '');
  const [antiPatterns, setAntiPatterns] = useState((workflow?.anti_patterns ?? []).join(', '));
  const [priority, setPriority] = useState(workflow?.priority ?? 100);
  const [saveAsDraft, setSaveAsDraft] = useState(!editingActive);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (workflow) {
      setSlug(workflow.slug);
      setTitle(workflow.title);
      setTriggers((workflow.triggers ?? []).join(', '));
      setStepsJson(stepsToJson(workflow.steps ?? DEFAULT_STEPS));
      setSuccessShape(workflow.success_shape ?? '');
      setAntiPatterns((workflow.anti_patterns ?? []).join(', '));
      setPriority(workflow.priority);
      setSaveAsDraft(workflow.status === 'draft');
    }
  }, [workflow]);

  const handleSave = async () => {
    if (!slug.trim() || !title.trim()) {
      toast.error('Slug and title are required');
      return;
    }

    let steps: WorkflowStep[];
    try {
      steps = parseSteps(stepsJson);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Invalid steps JSON');
      return;
    }

    const payload = {
      slug: slug.trim(),
      title: title.trim(),
      triggers: triggers
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      steps,
      success_shape: successShape.trim() || null,
      anti_patterns: antiPatterns
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      priority,
      status: saveAsDraft ? 'draft' : 'active',
      created_via: 'engine_ui',
      updated_at: new Date().toISOString(),
    };

    setSaving(true);
    try {
      if (isEdit && workflow && editingActive && !saveAsDraft) {
        const { error: supersedeError } = await supabase
          .from('agent_workflows')
          .update({
            status: 'superseded',
            updated_at: new Date().toISOString(),
          })
          .eq('id', workflow.id);
        if (supersedeError) throw supersedeError;

        const { data: inserted, error } = await supabase
          .from('agent_workflows')
          .insert({
            ...payload,
            version: (workflow.version ?? 1) + 1,
          })
          .select('id')
          .single();
        if (error) throw error;

        if (inserted?.id) {
          await supabase
            .from('agent_workflows')
            .update({ superseded_by: inserted.id })
            .eq('id', workflow.id);
        }
      } else if (isEdit && workflow && workflow.status === 'draft') {
        const { error } = await supabase
          .from('agent_workflows')
          .update(payload)
          .eq('id', workflow.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('agent_workflows').insert({
          ...payload,
          version: 1,
        });
        if (error) throw error;
      }

      toast.success(isEdit ? 'Workflow saved' : 'Workflow created');
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit workflow' : 'New workflow'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                disabled={isEdit}
                placeholder="morning_docket"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60 font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority (lower = higher)
              </label>
              <input
                type="number"
                min={1}
                max={999}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Triggers (comma-separated intent phrases)
            </label>
            <input
              type="text"
              value={triggers}
              onChange={(e) => setTriggers(e.target.value)}
              placeholder="morning, docket, what should I do today"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Steps (JSON array)
            </label>
            <textarea
              value={stepsJson}
              onChange={(e) => setStepsJson(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Success shape
            </label>
            <textarea
              value={successShape}
              onChange={(e) => setSuccessShape(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Anti-patterns (comma-separated)
            </label>
            <input
              type="text"
              value={antiPatterns}
              onChange={(e) => setAntiPatterns(e.target.value)}
              placeholder="materialize, send from MCP"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={saveAsDraft}
              onChange={(e) => setSaveAsDraft(e.target.checked)}
            />
            Save as draft (uncheck to activate — supersedes current active when editing)
          </label>

          {isEdit && editingActive && !saveAsDraft && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Saving creates a new active version and marks the current row superseded.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowModal;
